import React from "react";
import { useTable } from "react-table";
import { format } from "date-fns";
import { Tooltip } from "flowbite-react";
import { parseLastLogin } from "../../shared/lastLogin";
import type { TenantUser } from "../../shared/types/models";
import type { Column, CellProps } from "react-table";

export interface TenantUsersListProps {
  tenantUsers: TenantUser[];
  handleRowClick?: (tenantUser: TenantUser) => void;
  handleRowMouseOver?: (tenantUser: TenantUser) => void;
  searchTerm?: string;
}

export const TenantUsersList: React.FC<TenantUsersListProps> = (props) => {
  // Helper function to check if text needs truncation (based on 400px max-width)
  const shouldTruncate = (text: string, maxChars: number = 40) => {
    return text && text.length > maxChars
  }

  const highlightMatch = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm || !searchTerm.trim()) {
      return text;
    }

    const searchLower = searchTerm.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(searchLower);

    if (index === -1) {
      return text;
    }

    return (
      <>
        {text.substring(0, index)}
        <span className="bg-yellow-100 dark:bg-yellow-800">
          {text.substring(index, index + searchTerm.length)}
        </span>
        {text.substring(index + searchTerm.length)}
      </>
    );
  };

  const columns = React.useMemo<ReadonlyArray<Column<TenantUser>>>(
    () => [
      {
        Header: 'Email Address',
        accessor: 'emailAddress',
        Cell: ({ row: { original: tenantUser } }: CellProps<TenantUser, string>) => (
          <div className="flex items-baseline">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {highlightMatch(tenantUser.emailAddress, props.searchTerm || '')}
            </span>
            <span className={`ml-2 px-2 py-1 text-xs rounded ${tenantUser.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'}`}>
              {tenantUser.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        ),
      },
      {
        Header: 'Associated Tenants',
        accessor: 'tenantAccounts',
        Cell: ({ row: { original: tenantUser } }) => (
          <div className="space-y-1">
            {tenantUser.tenantAccounts.length > 0 ? (
              tenantUser.tenantAccounts.map((account) => {
                const accountDisplayName = account.accountName || account.accountNumber
                const needsTruncation = shouldTruncate(accountDisplayName)
                
                const content = (
                  <div className="max-w-[400px] truncate text-sm text-gray-700 dark:text-gray-200">
                    {highlightMatch(accountDisplayName, props.searchTerm || '')}
                  </div>
                )
                
                return (
                  <div key={account.id} className="flex items-center space-x-2">
                    {needsTruncation ? (
                      <Tooltip
                        content={accountDisplayName}
                        placement="bottom"
                        className="text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0"
                      >
                        {content}
                      </Tooltip>
                    ) : (
                      content
                    )}
                    <span className={`px-1 py-0.5 text-xs rounded ${account.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'}`}>
                      {account.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                )
              })
            ) : (
              <span className="text-sm text-gray-700 dark:text-gray-200">No tenants</span>
            )}
          </div>
        ),
      },
      {
        Header: 'Last Login',
        accessor: 'lastLogin',
        Cell: ({ row: { original: tenantUser } }: CellProps<TenantUser, string | null>) => {
          const lastLogin = parseLastLogin(tenantUser.lastLogin)
          return (
            <div className="flex flex-col">
              {lastLogin ? (
                <>
                  <span className="text-sm text-gray-700 dark:text-gray-200">{format(lastLogin, 'MMM d, yyyy')}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{format(lastLogin, 'h:mm aa')}</span>
                </>
              ) : (
                <span className="text-sm text-gray-700 dark:text-gray-200">N/A</span>
              )}
            </div>
          )
        },
      },
    ],
    [props.searchTerm]
  )

  const table = useTable({ data: props.tenantUsers, columns })

  return (
    <div className="flex flex-col">
      <div className="rounded-lg shadow overflow-x-auto">
        <div className="align-middle inline-block min-w-full">
          <div className="overflow-hidden border-b border-gray-200">
            <table {...table.getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-800">
                {table.headerGroups.map((headerGroup) => {
                  const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()

                  return (
                    <tr key={key} {...headerGroupProps}>
                      {headerGroup.headers.map((column) => {
                        const { key, ...headerProps } = column.getHeaderProps()

                        return (
                          <th
                            key={key}
                            {...headerProps}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            {column.render('Header') as React.ReactNode}
                          </th>
                        )
                      })}
                    </tr>
                  )
                })}
              </thead>
              <tbody {...table.getTableBodyProps()} className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {table.rows.map((row, rowIdx) => {
                  table.prepareRow(row)

                  const { key, ...rowProps } = row.getRowProps()

                  return (
                    <tr
                      key={key}
                      {...rowProps}
                      onClick={() => props.handleRowClick?.(row.original)}
                      onMouseOver={() => props.handleRowMouseOver?.(row.original)}
                      className={`cursor-pointer ${rowIdx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}`}
                    >
                      {row.cells.map((cell) => {
                        const { key, ...cellProps } = cell.getCellProps()

                        return (
                          <td key={key} {...cellProps} className="px-6 py-4 whitespace-nowrap">
                            {cell.render('Cell') as React.ReactNode}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
};